import { Link, Outlet, useLocation } from 'react-router-dom';
import { Button, cx } from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { resolveActiveMenuCode } from '@/lib/activeMenu';
import { useLogout, useMe } from '@/features/auth/useAuth';
import type { Menu } from '@/features/auth/types';

/**
 * 헤더·메뉴·본문이 공유하는 최대 폭.
 *
 * 세 곳이 같은 값이어야 세로줄이 맞는다. 하나만 바꾸면 헤더와 본문이 어긋난다.
 *
 * 처음에는 max-w-3xl(768px)이었다. 그것은 글을 읽는 화면의 폭이고
 * 이 앱은 전 화면이 표와 차트다 — 가계부 거래내역은 열이 5개, 추천 목록은
 * 종목명·지표 4종·점수를 한 줄에 놓는다. 데스크탑에서 좌우 여백만 넓고
 * 정작 표는 줄바꿈되는 상태였다.
 *
 * 5xl(1024px)은 노트북 화면(보통 1280~1440px)에서 양옆이 답답하지 않게 남고,
 * 표 열이 접히지 않는 선이다. 그 이상으로 넓히면 폰과 데스크탑의 정보 밀도가
 * 너무 벌어져 같은 화면을 두 번 설계해야 한다.
 *
 * 모바일은 영향이 없다. mx-auto 는 화면이 이 값보다 좁으면 아무 일도 하지 않는다.
 */
const CONTENT_WIDTH = 'mx-auto max-w-5xl';

/**
 * 탭을 균등 분할할 최대 개수.
 *
 * 3개까지는 화면을 나눠 채우고, 4개부터는 가로 스크롤로 둔다.
 *
 * 왜 상한이 필요한가
 *   폰 화면(약 360px)을 5로 나누면 탭 하나가 72px 다.
 *   "계좌·보유·체결" 은 그 폭에 절대 안 들어가 잘리거나 두 줄이 된다.
 *   균등 분할은 탭이 적을 때만 예뻐 보이는 방식이다.
 *
 * 관리자는 탭이 5개라 스크롤이 되고, 가계부만 쓰는 회원은 2~3개라 꽉 찬다.
 * 사람마다 다르게 보이는 것이 맞다 — 각자 화면에서 최선이면 된다.
 */
const EVEN_SPLIT_MAX = 3;

/**
 * 로그인 이후 화면의 공통 틀.
 *
 * 메뉴 목록은 서버가 준 것을 그대로 그린다 — 프론트에 메뉴 상수를 두면
 * 관리자가 권한을 바꿔도 없는 메뉴가 계속 보인다.
 *
 * 🔴 탭 위치를 화면 크기로 가른다 (2026-08-16)
 *   폰   하단 고정. 엄지가 닿는 자리다. 앱은 대부분 이렇게 되어 있다
 *   PC   상단. 하단 고정 바는 웹에서 어색하고 화면 세로를 낭비한다
 *
 *   한쪽으로 통일하면 반드시 한쪽이 불편해진다. 같은 목록을 두 번 그리지 않고
 *   MenuTabs 하나를 두 자리에 놓는다.
 */
export function AppLayout() {
  const { me } = useMe();
  const logoutMutation = useLogout();
  const location = useLocation();

  /*
    활성 탭은 언제나 하나다.

    NavLink 의 isActive 를 쓰지 않는 이유는 그것이 하위 경로까지 활성으로 보기 때문이다.
    /trading 탭이 /trading/account 에서도 켜져 두 탭이 동시에 활성이었다.
    판정 규칙과 그것을 그렇게 정한 이유는 lib/activeMenu.ts 에 있다.
  */
  const menus = visibleMenus(me);
  const activeCode = resolveActiveMenuCode(location.pathname, menus);

  return (
    /*
      pb-16 sm:pb-0 — 폰에서 하단 탭바가 본문 마지막 줄을 덮지 않게 자리를 비운다.
      이것이 없으면 목록 맨 아래 항목이 탭에 가려 영영 안 보인다.
    */
    <div className="min-h-dvh bg-surface-muted pb-16 sm:pb-0">
      <header className="border-b border-line bg-surface">
        <div className={cx(CONTENT_WIDTH, 'flex items-center justify-between gap-2 px-4 py-3')}>
          <BrandLink me={me} />

          <div className="flex items-center gap-1">
            {/* 알림. 로그인 전에는 아예 부르지 않는다 */}
            <NotificationBell enabled={me != null} />
            <ThemeToggle />
            <Button
              variant="ghost"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              로그아웃
            </Button>
          </div>
        </div>

        {/* 데스크탑 탭. 폰에서는 아래 고정 바가 대신한다 */}
        <div className="hidden sm:block">
          <MenuTabs menus={menus} activeCode={activeCode} placement="top" />
        </div>
      </header>

      <main className={cx(CONTENT_WIDTH, 'p-4')}>
        <Outlet />
      </main>

      {/*
        폰 하단 고정 탭.

        pb-[env(safe-area-inset-bottom)] 는 아이폰 홈 인디케이터 영역을 피한다.
        없으면 마지막 탭이 그 검은 막대에 반쯤 가려 눌리지 않는다.
        PWA 로 홈 화면에서 실행할 때 특히 드러난다.
      */}
      <nav className="fixed inset-x-0 bottom-0 z-10 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] sm:hidden">
        <MenuTabs menus={menus} activeCode={activeCode} placement="bottom" />
      </nav>
    </div>
  );
}

/**
 * 화면에 그릴 탭 목록. (3.6)
 *
 * 서버가 준 메뉴에 '내 정보' 를 덧붙인다.
 *
 * 🔴 '내 정보' 는 cm_menu 에 없다. 없는 것이 맞다
 *   메뉴는 "권한으로 끊는 단위" 인데, 자기 정보 수정은 끊을 대상이 아니다.
 *   메뉴로 만들면 관리자가 실수로 끄는 순간 그 사람은 자기 비밀번호도 못 바꾼다.
 *
 * 🔴 관리자에게는 주지 않는다
 *   관리자는 이미 탭이 5개다(종목·추천 / 계좌 / 가계부 / 혜택 / 관리자).
 *   하나를 더하면 폰에서 가로 스크롤이 길어지고, 자주 쓰는 앞쪽 탭이 밀린다.
 *   대신 관리자 화면 안에 같은 내용을 하위 탭으로 넣었다. (AdminPage)
 *
 *   /me 경로 자체는 관리자에게도 열려 있다. 주소로 들어오면 같은 화면이 뜬다.
 *   탭에 없는 것과 못 들어가는 것은 다르다.
 */
function visibleMenus(me: { admin: boolean; menus: Menu[] } | null | undefined): Menu[] {
  if (!me) {
    return [];
  }
  if (me.admin) {
    return me.menus;
  }
  return [...me.menus, { code: MY_INFO_MENU_CODE, name: '내 정보', path: '/me' }];
}

/**
 * '내 정보' 탭의 코드.
 *
 * cm_menu 의 어떤 code 와도 겹치면 안 된다. 겹치면 활성 탭 판정이 엉킨다.
 * 서버 코드는 전부 대문자 단어라(TRADING, LEDGER…) 접두사를 붙여 갈라둔다.
 */
const MY_INFO_MENU_CODE = 'CLIENT_ME';

/**
 * 로고와 앱 이름. 누르면 홈으로 간다.
 *
 * 어느 화면에서든 처음으로 돌아가는 길이 하나는 있어야 한다.
 * 대부분의 웹에서 좌측 상단이 그 자리이고, 사람들은 눌러보기 전에 이미 그렇게 기대한다.
 *
 * 로고 이미지는 파비콘과 같은 파일을 쓴다.
 * 헤더용을 따로 만들면 하나를 바꿨을 때 다른 하나가 남는다.
 */
function BrandLink({ me }: { me: { displayName: string; admin: boolean } | null | undefined }) {
  return (
    <Link
      to="/"
      className="flex items-center gap-2 rounded-lg transition-opacity hover:opacity-80"
      aria-label="홈으로"
    >
      <img
        src="/favicon-32.png"
        alt=""
        // alt 를 비워 스크린리더가 건너뛰게 한다. 옆의 글자가 이미 같은 것을 말한다.
        aria-hidden="true"
        className="h-8 w-8 shrink-0 rounded-lg"
      />
      <span>
        <span className="block text-base font-semibold text-fg">파크302호 개미들</span>
        {me && (
          <span className="block text-xs text-fg-subtle">
            {me.displayName}
            {me.admin && ' · 관리자'}
          </span>
        )}
      </span>
    </Link>
  );
}

/**
 * 메뉴 탭. 상단과 하단에서 같은 것을 쓴다.
 *
 * 🔴 활성 표시를 배경 채움이 아니라 글자색 + 막대로 한다 (2026-08-16)
 *   그전에는 검정 배경으로 채웠다. 균등 분할과 하단 배치를 넣으면서 바꿨다 —
 *   화면 폭의 33%를 차지하는 진한 덩어리가 세 개 생기면 화면이 무거워지고,
 *   하단 고정 바에서는 특히 그렇다.
 *
 *   글자를 브랜드색으로 굵게 하고 얇은 막대를 붙이면 어느 폭에서도 같은 무게로 읽힌다.
 *   막대 위치가 상단은 아래, 하단은 위인 것은 본문 쪽을 가리키기 위해서다.
 */
function MenuTabs({
  menus,
  activeCode,
  placement,
}: {
  menus: Menu[];
  activeCode: string | null;
  placement: 'top' | 'bottom';
}) {
  if (menus.length === 0) {
    return null;
  }

  const evenSplit = menus.length <= EVEN_SPLIT_MAX;

  return (
    <div
      className={cx(
        CONTENT_WIDTH,
        'px-4',
        // 균등 분할이 아니면 넘치는 만큼 가로로 스크롤한다.
        // 줄바꿈으로 처리하면 헤더 높이가 들쭉날쭉해진다.
        !evenSplit && 'overflow-x-auto',
      )}
    >
      <ul className={cx('flex', evenSplit ? 'w-full' : 'gap-1')}>
        {menus.map((menu) => {
          const active = menu.code === activeCode;

          return (
            <li key={menu.code} className={cx(evenSplit && 'flex-1')}>
              <Link
                to={menu.path}
                aria-current={active ? 'page' : undefined}
                className={cx(
                  'relative flex min-h-[44px] items-center justify-center whitespace-nowrap px-3 text-sm',
                  'transition-colors',
                  active ? 'font-semibold text-brand' : 'text-fg-muted hover:text-fg',
                )}
              >
                {menu.name}

                {/*
                  활성 막대. 글자 아래(또는 위)에 붙는 2px 선이다.
                  active 가 아닐 때 렌더링하지 않으면 글자 위치가 흔들리지 않는다 —
                  absolute 라 문서 흐름을 차지하지 않기 때문이다.
                */}
                {active && (
                  <span
                    aria-hidden="true"
                    className={cx(
                      'absolute inset-x-2 h-0.5 rounded-full bg-brand',
                      placement === 'top' ? 'bottom-0' : 'top-0',
                    )}
                  />
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
